import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { 
  Save, 
  Eye, 
  Smartphone, 
  Tablet, 
  Monitor, 
  Undo, 
  Redo,
  Settings,
  Download,
  Upload,
  AlertCircle,
  Layout,
  Plus
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ElementPalette from "./visual-builder/ElementPalette";
import DesignCanvas from "./visual-builder/DesignCanvas";
import PropertiesPanel from "./visual-builder/PropertiesPanel";
import PreviewFrame from "./visual-builder/PreviewFrame";

export interface PageElement {
  id: string;
  type: string;
  name: string;
  content: any;
  styles: {
    background?: string;
    padding?: string;
    margin?: string;
    textAlign?: string;
    backgroundColor?: string;
    color?: string;
    borderRadius?: string;
    border?: string;
    fontSize?: string;
    fontWeight?: string;
    width?: string;
    height?: string;
    [key: string]: any;
  };
  settings: {
    isVisible?: boolean;
    animation?: string;
    responsive?: {
      mobile?: any;
      tablet?: any;
      desktop?: any;
    };
    [key: string]: any;
  };
  children?: PageElement[];
  order: number;
}

export interface PageData {
  elements: PageElement[];
  globalStyles: {
    colors: {
      primary: string;
      secondary: string;
      accent: string;
      background: string;
      text: string;
    };
    fonts: {
      primary: string;
      secondary: string;
    };
    spacing: {
      small: string;
      medium: string;
      large: string;
    };
  };
  responsive: {
    breakpoints: {
      mobile: string;
      tablet: string;
      desktop: string;
    };
  };
}

interface VisualPageBuilderProps {
  initialData?: PageData;
  onSave: (data: PageData) => void;
  onPreview?: (data: PageData) => void;
}

// Simple fallback component for when drag and drop fails
function SimpleBuilder({ pageData, onSave }: { pageData: PageData, onSave: (data: PageData) => void }) {
  return (
    <div className="h-full flex flex-col bg-background min-h-[600px]">
      <div className="p-8 text-center">
        <div className="max-w-md mx-auto space-y-4">
          <Layout className="w-16 h-16 mx-auto text-muted-foreground" />
          <div>
            <h3 className="text-lg font-semibold">Visual Builder</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Der Visual Builder wird geladen. Dies kann einen Moment dauern.
            </p>
          </div>
          <Button onClick={() => onSave(pageData)}>
            Aktuelle Daten speichern
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function VisualPageBuilder({ 
  initialData, 
  onSave, 
  onPreview 
}: VisualPageBuilderProps) {
  const { toast } = useToast();
  
  const [pageData, setPageData] = useState<PageData>(
    initialData || {
      elements: [],
      globalStyles: {
        colors: {
          primary: "#3b82f6",
          secondary: "#6b7280",
          accent: "#10b981",
          background: "#ffffff",
          text: "#111827"
        },
        fonts: {
          primary: "Inter",
          secondary: "Inter"
        },
        spacing: {
          small: "8px",
          medium: "16px",
          large: "32px"
        }
      },
      responsive: {
        breakpoints: {
          mobile: "768px",
          tablet: "1024px",
          desktop: "1280px"
        }
      }
    }
  );
  
  const [selectedElement, setSelectedElement] = useState<PageElement | null>(null);
  const [activeView, setActiveView] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [showPreview, setShowPreview] = useState(false);
  const [history, setHistory] = useState<PageData[]>([pageData]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Save state to history for undo/redo
  const saveToHistory = useCallback((newPageData: PageData) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newPageData);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Update page data
  const updatePageData = useCallback((newData: Partial<PageData>) => {
    const updated = { ...pageData, ...newData };
    setPageData(updated);
    saveToHistory(updated);
  }, [pageData, saveToHistory]);

  // Add element to canvas
  const addElement = useCallback((elementType: string, dropIndex?: number) => {
    const newElement: PageElement = {
      id: `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: elementType,
      name: `New ${elementType}`,
      content: getDefaultContent(elementType),
      styles: getDefaultStyles(elementType),
      settings: { isVisible: true },
      order: dropIndex ?? pageData.elements.length
    };

    const newElements = [...pageData.elements];
    if (dropIndex !== undefined) {
      newElements.splice(dropIndex, 0, newElement);
      // Update order for following elements
      newElements.forEach((el, index) => {
        el.order = index;
      });
    } else {
      newElements.push(newElement);
    }

    updatePageData({ elements: newElements });
    setSelectedElement(newElement);
  }, [pageData.elements, updatePageData]);

  // Update selected element
  const updateElement = useCallback((elementId: string, updates: Partial<PageElement>) => {
    const newElements = pageData.elements.map(el => 
      el.id === elementId ? { ...el, ...updates } : el
    );
    updatePageData({ elements: newElements });
    
    if (selectedElement?.id === elementId) {
      setSelectedElement({ ...selectedElement, ...updates });
    }
  }, [pageData.elements, selectedElement, updatePageData]);

  // Delete element
  const deleteElement = useCallback((elementId: string) => {
    const newElements = pageData.elements.filter(el => el.id !== elementId);
    updatePageData({ elements: newElements });
    
    if (selectedElement?.id === elementId) {
      setSelectedElement(null);
    }
  }, [pageData.elements, selectedElement, updatePageData]);

  // Handle drag and drop
  const onDragEnd = (result: DropResult) => {
    try {
      if (!result.destination) return;

      const { source, destination } = result;
      
      // Handle drag from palette to canvas
      if (source.droppableId === 'palette' && destination.droppableId === 'canvas') {
        const elementType = result.draggableId;
        addElement(elementType, destination.index);
        return;
      }
      
      // Handle reordering within canvas
      if (source.droppableId === 'canvas' && destination.droppableId === 'canvas') {
        const newElements = [...pageData.elements];
        const [removed] = newElements.splice(source.index, 1);
        newElements.splice(destination.index, 0, removed);
        
        // Update order
        newElements.forEach((el, index) => {
          el.order = index;
        });
        
        updatePageData({ elements: newElements });
      }
    } catch (error) {
      console.error('Drag and drop error:', error);
    }
  };

  // Undo/Redo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setPageData(history[newIndex]);
      setHistoryIndex(newIndex);
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setPageData(history[newIndex]);
      setHistoryIndex(newIndex);
    }
  }, [history, historyIndex]);

  // Save page
  const handleSave = () => {
    try {
      onSave(pageData);
      toast({
        title: "Seite gespeichert",
        description: "Ihre Änderungen wurden erfolgreich gespeichert."
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Fehler beim Speichern",
        description: "Die Änderungen konnten nicht gespeichert werden.",
        variant: "destructive"
      });
    }
  };

  // Preview page
  const handlePreview = () => {
    try {
      setShowPreview(true);
      onPreview?.(pageData);
    } catch (error) {
      console.error('Preview error:', error);
      toast({
        title: "Fehler bei der Vorschau",
        description: "Die Vorschau konnte nicht geladen werden.",
        variant: "destructive"
      });
    }
  };


  return (
    <div className="h-full flex flex-col bg-background min-h-[600px]">
      {/* Toolbar */}
      <div className="border-b bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={undo}
              disabled={historyIndex === 0}
            >
              <Undo className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={redo}
              disabled={historyIndex === history.length - 1}
            >
              <Redo className="w-4 h-4" />
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <Button variant="outline" size="sm">
              <Upload className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>

          {/* Responsive View Controls */}
          <div className="flex items-center gap-1 border rounded-lg p-1">
            <Button
              variant={activeView === 'desktop' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('desktop')}
            >
              <Monitor className="w-4 h-4" />
            </Button>
            <Button
              variant={activeView === 'tablet' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('tablet')}
            >
              <Tablet className="w-4 h-4" />
            </Button>
            <Button
              variant={activeView === 'mobile' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('mobile')}
            >
              <Smartphone className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePreview}>
              <Eye className="w-4 h-4 mr-2" />
              Vorschau
            </Button>
            <Button onClick={handleSave}>
              <Save className="w-4 h-4 mr-2" />
              Speichern
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        <DragDropContext onDragEnd={onDragEnd}>
          {/* Left Panel - Element Palette */}
          <div className="w-80 border-r bg-card/30 flex flex-col">
            <div className="p-4 border-b">
              <h2 className="font-semibold">Elemente</h2>
              <p className="text-sm text-muted-foreground">
                Ziehen Sie Elemente auf die Arbeitsfläche
              </p>
            </div>
            <ScrollArea className="flex-1">
              <ElementPalette />
            </ScrollArea>
          </div>

          {/* Center - Design Canvas */}
          <div className="flex-1 flex flex-col">
            <DesignCanvas
              elements={pageData.elements}
              selectedElement={selectedElement}
              onSelectElement={setSelectedElement}
              onUpdateElement={updateElement}
              onDeleteElement={deleteElement}
              activeView={activeView}
              globalStyles={pageData.globalStyles}
            />
          </div>
        </DragDropContext>

        {/* Right Panel - Properties */}
        <div className="w-80 border-l bg-card/30 flex flex-col">
          <div className="p-4 border-b">
            <h2 className="font-semibold">Eigenschaften</h2>
            <p className="text-sm text-muted-foreground">
              {selectedElement ? selectedElement.name : 'Kein Element ausgewählt'}
            </p>
          </div>
          <ScrollArea className="flex-1">
            <PropertiesPanel
              selectedElement={selectedElement}
              globalStyles={pageData.globalStyles}
              onUpdateElement={updateElement}
              onUpdateGlobalStyles={(styles) => updatePageData({ globalStyles: styles })}
            />
          </ScrollArea>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <PreviewFrame
          pageData={pageData}
          onClose={() => setShowPreview(false)}
          activeView={activeView}
        />
      )}
    </div>
  );
}

// Helper functions for default content and styles
function getDefaultContent(elementType: string): any {
  switch (elementType) {
    case 'hero':
      return {
        title: 'Willkommen bei uns',
        subtitle: 'Entdecken Sie unsere großartigen Angebote',
        ctaText: 'Jetzt starten',
        ctaLink: '#',
        backgroundImage: null
      };
    case 'text':
      return {
        text: 'Hier steht Ihr Text. Klicken Sie hier zum Bearbeiten.',
        tag: 'p'
      };
    case 'button':
      return {
        text: 'Klicken Sie hier',
        link: '#',
        variant: 'primary'
      };
    case 'image':
      return {
        src: 'https://via.placeholder.com/400x300',
        alt: 'Platzhalter Bild',
        caption: ''
      };
    case 'benefits':
      return {
        title: 'Unsere Vorteile',
        items: [
          { icon: '✓', title: 'Vorteil 1', description: 'Beschreibung des ersten Vorteils' },
          { icon: '✓', title: 'Vorteil 2', description: 'Beschreibung des zweiten Vorteils' },
          { icon: '✓', title: 'Vorteil 3', description: 'Beschreibung des dritten Vorteils' }
        ]
      };
    case 'testimonial':
      return {
        quote: 'Das ist ein großartiger Service!',
        author: 'Max Mustermann',
        position: 'Kunde',
        avatar: 'https://via.placeholder.com/64x64'
      };
    case 'contact':
      return {
        title: 'Kontaktieren Sie uns',
        fields: [
          { name: 'name', label: 'Name', type: 'text', required: true },
          { name: 'email', label: 'E-Mail', type: 'email', required: true },
          { name: 'message', label: 'Nachricht', type: 'textarea', required: true }
        ],
        submitText: 'Nachricht senden'
      };
    case 'dropdown':
      return {
        label: 'Auswahl treffen',
        placeholder: 'Bitte wählen Sie...',
        options: [
          { value: 'option1', label: 'Option 1' },
          { value: 'option2', label: 'Option 2' },
          { value: 'option3', label: 'Option 3' }
        ],
        required: false
      };
    default:
      return {};
  }
}

function getDefaultStyles(elementType: string): any {
  const baseStyles = {
    padding: '16px',
    margin: '0px',
    backgroundColor: 'transparent',
    color: '#111827',
    borderRadius: '0px',
    border: 'none'
  };

  switch (elementType) {
    case 'hero':
      return {
        ...baseStyles,
        padding: '80px 32px',
        textAlign: 'center',
        backgroundColor: '#f9fafb',
        fontSize: '48px',
        fontWeight: 'bold'
      };
    case 'button':
      return {
        ...baseStyles,
        padding: '12px 24px',
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '500',
        textAlign: 'center',
        display: 'inline-block'
      };
    case 'text':
      return {
        ...baseStyles,
        fontSize: '16px',
        lineHeight: '1.6'
      };
    case 'image':
      return {
        ...baseStyles,
        padding: '0px',
        width: '100%',
        height: 'auto'
      };
    case 'dropdown':
      return {
        ...baseStyles,
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        fontSize: '14px',
        width: '100%',
        maxWidth: '300px'
      };
    default:
      return baseStyles;
  }
}